import { ChatEnvelope, ChatEnvelopeDecodeResult, ChatJsonCodec } from '../types/chat.types';

type ValidatorFn = (payload: unknown) => boolean;

interface ChatJsonCodecOptions<TEvents> {
  ns: string;
  v?: number;
  validators?: Partial<Record<keyof TEvents & string, ValidatorFn | { safeParse: (value: unknown) => { success: boolean } }>>;
}

const makeCorrelationId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

export function createChatJsonCodec<TEvents = Record<string, any>>(options: ChatJsonCodecOptions<TEvents>): ChatJsonCodec<TEvents> {
  const version = options.v ?? 1;

  const validate = (type: string, payload: unknown): boolean => {
    const rawValidator = options.validators?.[type as keyof TEvents & string];
    if (!rawValidator) return true;
    if (typeof rawValidator === 'function') {
      return rawValidator(payload);
    }
    if (rawValidator && typeof rawValidator === 'object' && typeof rawValidator.safeParse === 'function') {
      try {
        return !!rawValidator.safeParse(payload).success;
      } catch {
        return false;
      }
    }
    return true;
  };

  const encode = <K extends keyof TEvents & string>(type: K, payload: TEvents[K]) => {
    const correlationId = makeCorrelationId();
    const envelope: ChatEnvelope<TEvents[K]> = {
      ns: options.ns,
      v: version,
      type,
      correlationId,
      ts: Date.now(),
      payload,
    };
    return { text: JSON.stringify(envelope), correlationId };
  };

  const decode = (raw: string): ChatEnvelopeDecodeResult => {
    try {
      const parsed = JSON.parse(raw);
      const correlationId = parsed.correlationId ?? parsed.id;
      if (!correlationId || typeof correlationId !== 'string') {
        throw new Error('Missing correlationId');
      }
      const envelope: ChatEnvelope = {
        ns: parsed.ns,
        v: parsed.v,
        type: parsed.type,
        correlationId,
        ts: parsed.ts,
        payload: parsed.payload,
      };
      if (envelope.ns !== options.ns || envelope.v !== version) {
        throw new Error('Envelope namespace/version mismatch');
      }
      if (!validate(envelope.type, envelope.payload)) {
        throw new Error('Envelope payload failed validation');
      }
      return { ok: true, envelope, raw };
    } catch (error: any) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { ok: false, error: err, raw };
    }
  };

  return { encode, decode };
}
